import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { AnyRow } from "@/lib/rowTypes";

interface AssessmentQuestion {
  id: string;
  question_text: string;
  question_type: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  explanation: string | null;
  cognitive_level: string;
}

interface Assessment {
  id: string;
  assessment_type: string;
  title: string;
  description: string | null;
  course_name: string | null;
  skill_cluster: string | null;
  passing_score: number;
  time_limit_minutes: number | null;
  learning_outcomes: string[] | null;
}

interface AssessmentAttempt {
  id: string;
  score: number;
  max_score: number;
  passed: boolean;
  completed_at: string | null;
}

export function useAssessments() {
  const [loading, setLoading] = useState(false);

  const getAssessmentsForCourse = useCallback(async (courseName: string) => {
    const { data } = await supabase
      .from("assessments")
      .select("*")
      .eq("course_name", courseName)
      .order("trigger_after_lesson_order");
    return (data || []) as Assessment[];
  }, []);

  const getAssessmentQuestions = useCallback(async (assessmentId: string) => {
    const { data } = await supabase
      .from("assessment_questions")
      .select("*")
      .eq("assessment_id", assessmentId)
      .order("sort_order");
    return (data || []) as AssessmentQuestion[];
  }, []);

  const getUserAttempts = useCallback(async (assessmentId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return [];
    const { data } = await supabase
      .from("assessment_attempts")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("assessment_id", assessmentId)
      .order("created_at", { ascending: false });
    return (data || []) as AssessmentAttempt[];
  }, []);

  const submitAttempt = useCallback(async (
    assessmentId: string,
    score: number,
    maxScore: number,
    answers: AnyRow[]
  ) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    const passed = maxScore > 0 ? (score / maxScore) * 100 >= 80 : false;
    const { data } = await (supabase
      .from("assessment_attempts")
      .insert({
        user_id: session.user.id,
        assessment_id: assessmentId,
        score,
        max_score: maxScore,
        passed,
        answers,
        completed_at: new Date().toISOString(),
      }))
      .select()
      .single();
    return data as AssessmentAttempt | null;
  }, []);

  const getSkillClusters = useCallback(async () => {
    const { data } = await supabase
      .from("skill_clusters")
      .select("*");
    return (data || []);
  }, []);

  return {
    loading,
    getAssessmentsForCourse,
    getAssessmentQuestions,
    getUserAttempts,
    submitAttempt,
    getSkillClusters,
  };
}
